import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetchNearbyPages} from "services/query-client";
import {PlusIcon, WifiIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import {EyeIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Dropdown from "components/Dropdown";
import {Beacon} from "types/Beacon";
import {useNavigate} from "react-router-dom";
import DeleteBeacon from '../Delete';
import BatteryLevel from "components/BatteryLevel";
import Button from 'components/Button';

function BeaconList() {
    const navigate = useNavigate()
    const [page, setPage] = useState<number>(1);
    const result = useQuery(['beacons.all', {page}], () => ngage.beacons.all(page), {keepPreviousData: true})

    const [deletingBeacon, setDeletingBeacon] = useState<Beacon | null>(null);

    // Prefetch next and previous pages
    prefetchNearbyPages('beacons.all', page, ngage.beacons.all)

    // Prepare page actions
    const actions = <Button icon={PlusIcon} onClick={() => navigate('/beacons/new')}>New</Button>

    return (
        <Page
          title="Beacons"
          subTitle={`Bluetooth hardware for interactive engagement`}
          breadcrumbs={{'Operations': '/', 'Beacons': '/beacons'}}
          actions={actions}
        >
            <Table paginationData={result.data} setPage={setPage}>
                <TableHead>
                    <TableRow>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>MAC Address</TableHeader>
                        <TableHeader>Type</TableHeader>
                        <TableHeader>Make/Model</TableHeader>
                        <TableHeader>Version</TableHeader>
                        <TableHeader>Battery</TableHeader>
                        <TableHeader>Added</TableHeader>
                        <TableHeader/>
                    </TableRow>
                </TableHead>

                <TableBody isLoading={result.isLoading}>

                    {result?.data && result.data.data.length < 1?
                        <TableRow index={0}>
                            <TableData colSpan={9}>
                                <div className="py-4 flex flex-col justify-center items-center">
                                    <WifiIcon className="h-8 w-8 text-gray-400 mb-2"/>
                                    <span className="text-sm font-medium text-gray-600">No beacons</span>
                                </div>
                            </TableData>
                        </TableRow>
                    : result?.data?.data.map((beacon, index) => (
                        <TableRow key={beacon.id} index={index} highlight>
                            <TableData>
                                <div className="flex">
                                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                                        {/* TODO: Convert to component */}
                                        <Dropdown position="left">
                                            {[
                                                {name: 'View', icon: EyeIcon, onClick: () => navigate(`/beacons/${beacon.id}`)},
                                                {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/beacons/${beacon.id}/edit`)},
                                                {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingBeacon(beacon), disabled: beacon.trigger_count > 0}
                                            ]}
                                        </Dropdown>
                                    </div>

                                    <div className="flex group-hover:translate-x-1 transition-all">
                                        <WifiIcon className="h-5 w-5 mr-2 text-gray-500" /> {beacon.name}
                                    </div>
                                </div>
                            </TableData>
                            <TableData>{beacon.mac_address}</TableData>
                            <TableData>{beacon.frame_type}</TableData>
                            <TableData>{beacon.make} {beacon.model}</TableData>
                            <TableData>{beacon.version}</TableData>
                            <TableData>
                                <BatteryLevel percent={beacon.battery_level} />
                            </TableData>
                            <TableData>{formattedDateTime(beacon.created_at)}</TableData>
                            <TableData alignment={TableData.alignments.RIGHT}>
                                {/* TODO: Convert to component */}
                                <Dropdown>
                                    {[
                                        {name: 'View', icon: EyeIcon, onClick: () => navigate(`/beacons/${beacon.id}`)},
                                        {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/beacons/${beacon.id}/edit`)},
                                        {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingBeacon(beacon), disabled: beacon.trigger_count > 0}
                                    ]}
                                </Dropdown>

                                {/* Delete confirmation modal */}
                                <DeleteBeacon
                                    beacon={deletingBeacon}
                                    show={beacon === deletingBeacon}
                                    onCancel={() => setDeletingBeacon(null)}
                                    onDelete={() => navigate('/beacons')}
                                    onDeleteFail={() => setDeletingBeacon(null)}
                                />
                            </TableData>
                        </TableRow>
                    ))}

                </TableBody>
            </Table>
        </Page>
    );
}

export default BeaconList;